import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBagShopping,
  faBolt,
  faBoxesStacked,
  faChartColumn,
  faChartLine,
  faCheckCircle,
  faGlobe,
  faHeart,
  faLayerGroup,
  faMagnifyingGlass,
  faPalette,
  faPuzzlePiece,
  faReceipt,
  faRocket,
  faShieldHalved,
  faShoppingCart,
  faSliders,
  faStore,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import LoadableImage from "../components/UI/LoadableImage";
import Style from "./WarenexEcommerce.module.css";
import { useI18n } from "../i18n/I18nContext";

const CALENDLY_URL = "https://calendly.com/contact-warenex/30min";

const advantageVisuals = [
  { icon: faShoppingCart, tone: "violet" },
  { icon: faRocket, tone: "blue" },
  { icon: faShieldHalved, tone: "green" },
  { icon: faPuzzlePiece, tone: "orange" },
  { icon: faChartColumn, tone: "purple" },
  { icon: faGlobe, tone: "sky" },
];

const platformAreaVisuals = [
  { icon: faStore, tone: "green" },
  { icon: faBoxesStacked, tone: "blue" },
  { icon: faPalette, tone: "coral" },
];

const workflowIcons = [faLayerGroup, faSliders, faBagShopping, faChartLine];

const openCalendly = () => {
  window.open(CALENDLY_URL);
};

const WarenexEcommerce = () => {
  const { copy } = useI18n();
  const wecpCopy = copy.wecp;
  const advantages = wecpCopy.advantages.items.map((item, index) => ({
    ...item,
    ...advantageVisuals[index],
  }));
  const platformAreas = wecpCopy.capabilities.areas.map((area, index) => ({
    ...area,
    ...platformAreaVisuals[index],
  }));
  const workflowSteps = wecpCopy.workflow.steps.map((step, index) => ({
    ...step,
    icon: workflowIcons[index],
  }));

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, []);

  return (
    <div className={Style.page}>
      <section className={Style.hero} aria-labelledby="ecommerce-title">
        <div className={Style["hero__content"]}>
          <h1 id="ecommerce-title">{wecpCopy.hero.title}</h1>

          <p>{wecpCopy.hero.description}</p>

          <button className={Style["demo-button"]} onClick={openCalendly}>
            {copy.common.requestDemo}
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </button>

          <ul
            className={Style["trust-list"]}
            aria-label={wecpCopy.hero.highlightsLabel}
          >
            {wecpCopy.hero.highlights.map((highlight) => (
              <li key={highlight}>
                <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                {highlight}
              </li>
            ))}
          </ul>
        </div>

        <div className={Style["hero__image-wrap"]}>
          <LoadableImage
            containerClassName={Style["product-image-loader"]}
            imgClassName={Style["product-image"]}
            src="/wecp.png"
            alt={wecpCopy.hero.imageAlt}
          />
        </div>
      </section>

      <section className={Style.advantages} aria-labelledby="advantages-title">
        <p className={Style.eyebrow}>{wecpCopy.advantages.eyebrow}</p>
        <h2 id="advantages-title">{wecpCopy.advantages.title}</h2>

        <div className={Style["advantages__grid"]}>
          {advantages.map((advantage) => (
            <article className={Style.card} key={advantage.title}>
              <div
                className={`${Style["card__icon"]} ${
                  Style[`card__icon--${advantage.tone}`]
                }`}
              >
                <FontAwesomeIcon icon={advantage.icon} aria-hidden="true" />
              </div>
              <div>
                <h3>{advantage.title}</h3>
                <p>{advantage.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        className={Style["connected-commerce"]}
        aria-labelledby="connected-commerce-title"
      >
        <div className={Style["connected-commerce__inner"]}>
          <div className={Style["connected-commerce__copy"]}>
            <p className={Style.eyebrow}>
              {wecpCopy.connectedCommerce.eyebrow}
            </p>
            <h2 id="connected-commerce-title">
              {wecpCopy.connectedCommerce.title}
            </h2>
            <p className={Style["section-intro"]}>
              {wecpCopy.connectedCommerce.description}
            </p>

            <ul className={Style["connected-commerce__points"]}>
              {wecpCopy.connectedCommerce.points.map((point) => (
                <li key={point}>
                  <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className={Style["workspace-graphic"]} aria-hidden="true">
            <div
              className={`${Style["mock-window"]} ${Style["mock-window--store"]}`}
            >
              <div className={Style["mock-window__header"]}>
                <span className={Style["mock-window__dots"]}>
                  <i />
                  <i />
                  <i />
                </span>
                <strong>{wecpCopy.connectedCommerce.storefront}</strong>
                <FontAwesomeIcon icon={faStore} />
              </div>
              <div className={Style["storefront-mock"]}>
                <div className={Style["mock-search"]}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                  <span>{wecpCopy.connectedCommerce.searchCatalog}</span>
                </div>
                <div className={Style["mock-product-list"]}>
                  {wecpCopy.connectedCommerce.productGroups.map(
                    (label, index) => (
                      <div
                        className={Style["mock-product-row"]}
                        key={label}
                      >
                        <span
                          className={`${Style["mock-product-thumb"]} ${
                            Style[`mock-product-thumb--${index + 1}`]
                          }`}
                        />
                        <span className={Style["mock-product-copy"]}>
                          <strong>{label}</strong>
                          <i />
                        </span>
                        <FontAwesomeIcon icon={faHeart} />
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            <div
              className={`${Style["mock-window"]} ${Style["mock-window--admin"]}`}
            >
              <div className={Style["mock-window__header"]}>
                <span className={Style["mock-window__dots"]}>
                  <i />
                  <i />
                  <i />
                </span>
                <strong>{wecpCopy.connectedCommerce.adminWorkspace}</strong>
                <FontAwesomeIcon icon={faChartColumn} />
              </div>
              <div className={Style["admin-mock"]}>
                <div className={Style["admin-mock__navigation"]}>
                  <span className={Style["admin-mock__nav-active"]}>
                    <FontAwesomeIcon icon={faChartLine} />
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faBoxesStacked} />
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faReceipt} />
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faUsers} />
                  </span>
                </div>
                <div className={Style["admin-mock__content"]}>
                  <div className={Style["admin-mock__summary"]}>
                    <span>
                      <FontAwesomeIcon icon={faReceipt} />
                      {wecpCopy.connectedCommerce.orders}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faBoxesStacked} />
                      {wecpCopy.connectedCommerce.inventory}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faUsers} />
                      {wecpCopy.connectedCommerce.customers}
                    </span>
                  </div>
                  <div className={Style["admin-mock__chart"]}>
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                  <div className={Style["admin-mock__chart-label"]}>
                    <span>{wecpCopy.connectedCommerce.salesOverview}</span>
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                </div>
              </div>
            </div>

            <span className={Style["workspace-graphic__connection"]}>
              <FontAwesomeIcon icon={faBolt} />
            </span>
          </div>
        </div>
      </section>

      <section className={Style.capabilities} aria-labelledby="capabilities-title">
        <div className={Style["section-heading"]}>
          <p className={Style.eyebrow}>{wecpCopy.capabilities.eyebrow}</p>
          <h2 id="capabilities-title">{wecpCopy.capabilities.title}</h2>
          <p>{wecpCopy.capabilities.description}</p>
        </div>

        <div className={Style["capabilities__grid"]}>
          {platformAreas.map((area) => (
            <article className={Style["capability"]} key={area.title}>
              <div
                className={`${Style["capability__icon"]} ${
                  Style[`capability__icon--${area.tone}`]
                }`}
              >
                <FontAwesomeIcon icon={area.icon} aria-hidden="true" />
              </div>
              <h3>{area.title}</h3>
              <p>{area.description}</p>
              <ul>
                {area.features.map((feature) => (
                  <li key={feature}>
                    <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className={Style.workflow} aria-labelledby="workflow-title">
        <div className={Style["section-heading"]}>
          <p className={Style.eyebrow}>{wecpCopy.workflow.eyebrow}</p>
          <h2 id="workflow-title">{wecpCopy.workflow.title}</h2>
          <p>{wecpCopy.workflow.description}</p>
        </div>

        <ol className={Style["workflow__steps"]}>
          {workflowSteps.map((step, index) => (
            <li key={step.title}>
              <span className={Style["workflow__number"]}>
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className={Style["workflow__icon"]}>
                <FontAwesomeIcon icon={step.icon} aria-hidden="true" />
              </div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className={Style.cta} aria-labelledby="cta-title">
        <div className={Style["cta__content"]}>
          <h2 id="cta-title">{wecpCopy.cta.title}</h2>
          <p>{wecpCopy.cta.description}</p>
          <button className={Style["cta__button"]} onClick={openCalendly}>
            {copy.common.requestDemo}
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </button>
        </div>

        <div className={Style["cta__image"]}>
          <LoadableImage
            containerClassName={Style["cta__image-loader"]}
            imgClassName={Style["cta__image-element"]}
            src="/wecp.png"
            alt={wecpCopy.cta.imageAlt}
          />
        </div>

        <FontAwesomeIcon
          className={Style["cta__accent"]}
          icon={faBolt}
          aria-hidden="true"
        />
      </section>
    </div>
  );
};

export default WarenexEcommerce;
